import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useState } from "react";
import { useNavigate } from "react-router";

import { SiteSelector } from "src/app/features/TerminalImportLog/components/tables/SiteSelector";
import { type ExtendedColumnsType, useGetTableDataQuery } from "../../../../../components";
import { PropertyTable } from "../../../../../components/Table/components/PropertyTable";
import { displayStatusValue } from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import type { TerminalImportLogItemEntity } from "../../types/TerminalImportLogItemEntity";

interface TerminalImportLogItemProps {
    id?: string;
}

export const TerminalImportLogItemTable: FC<TerminalImportLogItemProps> = ({ id }) => {
    const navigate = useNavigate();

    const [siteIdInput, setSiteIdInput] = useState<string>("");
    const [searchSiteId, setSearchSiteId] = useState<string | null>(null);

    const selectedDataGroupCode = getAssignedDataGroup()?.code;
    const baseURI = `${ResourceName.DATA_GROUPS}/${selectedDataGroupCode}`;
    const resourceFetchPath = `${baseURI}/${ResourceName.TERMINAL_IMPORT_LOG_ITEM}`;
    const siteIdParam = searchSiteId ? `&siteId=${encodeURIComponent(searchSiteId)}` : "";

    const fetchDataQuery = {
        query: `&status=true&terminalImportLogId=${id}${siteIdParam}`,
        resourceFetchPath,
        dataMapperKey: "",
    };

    const { data, isLoading } = useGetTableDataQuery(fetchDataQuery, {
        skip: id === undefined,
    });

    const onRowClick = (record: TerminalImportLogItemEntity) => {
        navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${record.terminalSystemId}`);
    };

    const getColumnsConfig = (): ExtendedColumnsType<TerminalImportLogItemEntity>[] => {
        return [
            {
                title: i18n.t("terminalImportLogItemList.terminalId"),
                key: "terminalId",
                dataIndex: "terminalId",
            },
            {
                title: i18n.t("terminalImportLogItemList.terminalStatus"),
                key: "terminalStatus",
                dataIndex: "terminalStatus",
                render: displayStatusValue,
            },
            {
                title: i18n.t("terminalImportLogItemList.terminalName"),
                key: "terminalName",
                dataIndex: "terminalName",
            },
            {
                title: i18n.t("terminalImportLogItemList.stationId"),
                key: "stationId",
                dataIndex: "stationId",
            },
            {
                title: i18n.t("terminalImportLogItemList.termProfileSysId"),
                key: "terminalProfileId",
                dataIndex: "terminalProfileId",
            },
        ];
    };

    const handleSearch = () => setSearchSiteId(siteIdInput);
    const handleReset = () => {
        setSearchSiteId(null);
        setSiteIdInput("");
    };

    return (
        <>
            <SiteSelector
                searchCallback={handleSearch}
                resetCallback={handleReset}
                siteIdInput={siteIdInput}
                setSiteIdInput={setSiteIdInput}
            />

            <Spin spinning={isLoading}>
                <PropertyTable<TerminalImportLogItemEntity>
                    name={"terminalImportLogItem_table"}
                    getColumnsConfig={getColumnsConfig}
                    propertyAsRowKey={"id"}
                    tableData={data?.list || []}
                    readOnly={false}
                    onRow={(record) => ({
                        onClick: () => onRowClick(record),
                    })}
                    rowsAreSelectable={false}
                />
            </Spin>
        </>
    );
};
